// @ts-nocheck
/*
 * Create / Edit Extension — v1 logic preserved end-to-end
 * (dual-mode, company code + role prefix fetch with parent-lookup for
 * call-centre admins, extension digit rule 1–4 then up to 4 digits,
 * 4-char min password, edit blocks allocated + locks extension).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Loader2, Save, ArrowLeft, Radio } from 'lucide-react';
import { postApiCall } from '@/service/postApiCall';
import { getApiCall } from '@/service/httpCall';
import { API_EXTENSION, API_USERS_PROFILE } from '@/service/url';
import { getUser } from '@/utilities/setting';
import { USER_ROLES } from '@/utilities/constant';
import { getDefaultExtensionFieldError, sortExtensionsAscending } from '@/utilities/helpers';
import Loader from '@/components/partials/components/Loader';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';

const cx = (...args) => args.filter(Boolean).join(' ');
const invalidCls = 'ring-2 ring-destructive/40';

const CreateExtension = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [companyCode, setCompanyCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [extension, setExtension] = useState('');
  const [description, setDescription] = useState('');
  const [rolePrefix, setRolePrefix] = useState('');
  const [, setOriginalExtension] = useState('');
  const [buttonDisable, setButtonDisable] = useState(false);
  const [extensionError, setExtensionError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const existingExtensionsRef = useRef([]);
  const [existingExtensionsTick, setExistingExtensionsTick] = useState(0);

  const generateUsername = (companyCodeArg, extensionCode, extensionInput) => {
    if (!extensionCode || !extensionInput) return '';
    const paddedExtensionCode = String(extensionCode).padStart(2, '0');
    return `${companyCodeArg}${paddedExtensionCode}${extensionInput}`;
  };

  useEffect(() => {
    const fetchCompanyCode = async () => {
      try {
        const data = getUser();
        if (!data || !data.id) {
          toast.error('User data not found. Please login again.');
          return;
        }
        const prefix =
          data.prefix === null || data.prefix === undefined || data.prefix === 0 ? 1 : Number(data.prefix);
        setRolePrefix(String(prefix).padStart(2, '0'));
        const isCallCenterAdmin =
          data?.role === USER_ROLES.CALL_CENTRE_ADMIN || data?.role === USER_ROLES.CALL_CENTRE_TEAM_LEAD;

        let companyCodeValue: any =
          data?.assignedCompany?.company_code ||
          data?.company?.company_code ||
          data?.assigned_company_details?.company_code ||
          null;

        if (isCallCenterAdmin && !companyCodeValue) {
          try {
            const currentUserProfileUrl = API_USERS_PROFILE.replace(':id', data.id);
            const currentUserProfile: any = await getApiCall(currentUserProfileUrl, {});
            const parentId = currentUserProfile?.parent_id || null;
            if (parentId) {
              const parentProfileUrl = API_USERS_PROFILE.replace(':id', parentId);
              const parentData: any = await getApiCall(parentProfileUrl, {});
              companyCodeValue =
                parentData?.assigned_company_details?.company_code ||
                parentData?.assignedCompany?.company_code ||
                parentData?.company?.company_code ||
                null;
              if (companyCodeValue) {
                console.log('Using parent company code for call center admin:', companyCodeValue);
              }
            }
          } catch (parentError) {
            console.error('Error fetching parent user data:', parentError);
          }
        }

        if (!companyCodeValue) {
          try {
            const currentUserProfileUrl = API_USERS_PROFILE.replace(':id', data.id);
            const currentUserProfile: any = await getApiCall(currentUserProfileUrl, {});
            companyCodeValue =
              currentUserProfile?.assigned_company_details?.company_code ||
              currentUserProfile?.assignedCompany?.company_code ||
              currentUserProfile?.company?.company_code ||
              null;
          } catch (profileError) {
            console.error('Error fetching current user profile:', profileError);
          }
        }

        if (companyCodeValue) {
          const paddedCompanyCode = String(companyCodeValue).padStart(4, '0');
          setCompanyCode(paddedCompanyCode);
        } else {
          console.warn('Company code not found in user data');
          toast.error('Company code not found. Please contact administrator.');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        toast.error('Failed to fetch user data.');
      }
    };
    fetchCompanyCode();
  }, []);

  useEffect(() => {
    if (isEditMode) return;
    getApiCall(`${API_EXTENSION}?data=all`)
      .then((data: any) => {
        existingExtensionsRef.current = sortExtensionsAscending(Array.isArray(data) ? data : []);
        setExistingExtensionsTick((t) => t + 1);
      })
      .catch(() => {
        existingExtensionsRef.current = [];
      });
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode) return;
    const namespace = `${companyCode || ''}${String(rolePrefix || '').padStart(2, '0')}`;
    setExtensionError(
      getDefaultExtensionFieldError(extension, existingExtensionsRef.current, namespace),
    );
  }, [extension, companyCode, rolePrefix, existingExtensionsTick, isEditMode]);

  useEffect(() => {
    if (!isEditMode && rolePrefix && extension && companyCode) {
      setUsername(generateUsername(companyCode, rolePrefix, extension));
    }
  }, [extension, rolePrefix, isEditMode, companyCode]);

  useEffect(() => {
    if (isEditMode && id) {
      setLoading(true);
      getApiCall(`${API_EXTENSION}/${id}`)
        .then((data: any) => {
          if (data) {
            if (data.is_allocated === 1) {
              toast.error('Cannot edit allocated extension. Only unallocated extensions can be edited.');
              navigate('/extension');
              return;
            }
            setUsername(data.username || '');
            setPassword('');
            setDescription(data.description || '');
            setOriginalExtension(String(data.extension || ''));
          } else {
            toast.error('No extension data found');
            navigate('/extension');
          }
        })
        .catch((err: any) => {
          console.error('Error fetching extension:', err);
          toast.error(friendlyApiError(err) || 'Failed to fetch extension data.');
          navigate('/extension');
        })
        .finally(() => setLoading(false));
    }
  }, [isEditMode, id, navigate]);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError(value && value.length < 4 ? 'Password must be at least 4 characters long' : '');
  };

  const handleExtensionChange = (e) => {
    const input = e.target.value;
    if (/^(?:[1-4][0-9]{0,3})?$/.test(input)) {
      setExtension(input);
    } else {
      setExtensionError('First digit must be 1–4 (not 0); remaining digits may be 0–9. Max 4 digits.');
    }
  };

  const blockInvalidKeys = (e) => {
    if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault();
  };

  const handleSaveExtension = async () => {
    if (extensionError) return void toast.error('Please fix the extension error before submitting.');
    if (!isEditMode && !extension) {
      setExtensionError('Extension is required');
      toast.error('Please enter an extension number.');
      return;
    }
    if (!isEditMode && (!password || password.length < 4)) {
      setPasswordError('Password must be at least 4 characters long');
      toast.error('Password must be at least 4 characters long.');
      return;
    }
    if (!username) return void toast.error('Username is required.');

    const payload: any = { username, password: password || '' };
    if (description && description.trim()) payload.description = description.trim();

    if (!isEditMode) {
      if (!companyCode) return void toast.error('Company code is required. Please refresh the page.');
      const fullExtensionStr = `${companyCode}${rolePrefix.padStart(2, '0')}${extension}`;
      payload.extension = fullExtensionStr;
    }

    try {
      setButtonDisable(true);
      if (isEditMode) {
        await postApiCall(`${API_EXTENSION}/${id}`, payload);
        toast.success('Extension updated successfully');
      } else {
        await postApiCall(API_EXTENSION, payload);
        toast.success('Extension created successfully');
      }
      setTimeout(() => navigate('/extension'), 3000);
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} extension:`, err);
      toast.error(
        friendlyApiError(err) ||
          `Failed to ${isEditMode ? 'update' : 'create'} extension.`,
      );
    } finally {
      setButtonDisable(false);
    }
  };

  return (
    <ScrollPage>
      <PageHeader
        title={isEditMode ? 'Edit Extension' : 'Create Extension'}
        description="Provision a SIP extension — company code, extension number, and password."
        actions={
          <Button asChild variant="outline">
            <Link to="/extension">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="mb-4 flex items-center gap-3 border-b border-border/50 pb-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/25">
            <Radio className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground">
              {isEditMode ? 'Update extension' : 'New extension'}
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Username is auto-generated from company code + role prefix + extension.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading extension…
          </div>
        ) : (
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <label
                  htmlFor="companyCode"
                  className="text-xs font-semibold text-foreground"
                >
                  Company code <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  id="companyCode"
                  value={companyCode || 'Loading…'}
                  readOnly
                  disabled
                  className="input-embossed h-10 w-full rounded-md px-3 font-mono text-sm tracking-wider opacity-70"
                />
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="extension"
                  className="text-xs font-semibold text-foreground"
                >
                  Enter extension <span className="text-destructive">*</span>
                </label>
                <div className="flex overflow-hidden rounded-md ring-1 ring-inset ring-border/60">
                  <span className="inline-flex items-center bg-muted px-2.5 font-mono text-xs font-semibold text-muted-foreground">
                    {rolePrefix.padStart(2, '0')}
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[1-4][0-9]{0,3}"
                    maxLength={4}
                    value={extension}
                    onKeyDown={blockInvalidKeys}
                    onChange={handleExtensionChange}
                    placeholder="e.g. 1099"
                    disabled={isEditMode}
                    readOnly={isEditMode}
                    className={cx(
                      'input-embossed h-10 flex-1 rounded-none border-0 px-3 font-mono text-sm tracking-wider shadow-none',
                      extensionError && invalidCls,
                    )}
                  />
                </div>
                {extensionError ? (
                  <p className="text-[11px] text-destructive">{extensionError}</p>
                ) : isEditMode ? (
                  <p className="text-[11px] text-muted-foreground">
                    Extension number cannot be changed.
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    First digit 1–4, rest 0–9. Max 4 digits. Must be unique.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="username"
                  className="text-xs font-semibold text-foreground"
                >
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  autoComplete="off"
                  value={username}
                  readOnly
                  disabled={!isEditMode}
                  placeholder="Auto-generated"
                  className="input-embossed h-10 w-full rounded-md px-3 font-mono text-sm tracking-wider opacity-70"
                />
                <p className="text-[11px] italic text-muted-foreground">
                  Company code + role prefix + entered extension
                </p>
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <label
                  htmlFor="password"
                  className="text-xs font-semibold text-foreground"
                >
                  Password{' '}
                  {!isEditMode && <span className="text-destructive">*</span>}
                </label>
                <input
                  type="password"
                  id="password"
                  autoComplete="off"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder={
                    isEditMode
                      ? 'Leave blank to keep current'
                      : 'Min 4 characters'
                  }
                  className={cx(
                    'input-embossed h-10 w-full rounded-md px-3 text-sm',
                    passwordError && invalidCls,
                  )}
                />
                {passwordError && (
                  <p className="text-[11px] text-destructive">{passwordError}</p>
                )}
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <label
                  htmlFor="description"
                  className="text-xs font-semibold text-foreground"
                >
                  Description{' '}
                  <span className="text-muted-foreground">(optional)</span>
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Note about this extension…"
                  rows={3}
                  className="input-embossed w-full rounded-md p-3 text-sm"
                />
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
              <Button asChild variant="outline">
                <Link to="/extension">Cancel</Link>
              </Button>
              <Button
                onClick={handleSaveExtension}
                type="button"
                disabled={buttonDisable || (!isEditMode && !!extensionError)}
                className="gap-1.5"
              >
                {buttonDisable ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {buttonDisable
                  ? isEditMode
                    ? 'Updating…'
                    : 'Creating…'
                  : isEditMode
                    ? 'Save changes'
                    : 'Create extension'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </ScrollPage>
  );
};

export default CreateExtension;
